'use client';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import YouTubeEmbed from '@/components/ui/YouTubeEmbed/YouTubeEmbed';

interface Props {
  videoId: string;
  open: boolean;
  onClose: () => void;
}

export const VideoModal = ({ videoId, open, onClose }: Props) => {
  const modalRef = useRef<HTMLDivElement>(null);
  // Portal target only exists client-side; render nothing during SSR.
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [open, onClose]);

  if (!open || !isMounted) return null;

  // Portaled to <body> (like EmailModal) so ancestor stacking contexts — e.g.
  // the guide hero's positioned breadcrumb header — can't paint over the
  // overlay.
  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
      <div
        ref={modalRef}
        className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-3xl max-h-[calc(100dvh-2rem)] rounded-xl shadow-xl flex flex-col p-4 sm:p-6 gap-4"
      >
        <div className="flex justify-end items-center shrink-0">
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <YouTubeEmbed videoId={videoId} fullWidth autoplay />
      </div>
    </div>,
    document.body,
  );
};
